import React, { useEffect, useState } from "react"
import { Alert, Button, FormFeedback, FormGroup, FormText, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Table } from "reactstrap"
import { get } from "helpers/api_helper"
import { lineName, logTime, plural, quantityLabel } from "./labels"
import { blockerText, contentKey, courierNames, itemName, mainShipment, nothingToSend, reasonHint, shipmentWeightLabel, warningText, weightLabel }
  from "./shipmentLabels"
import { logisticsMessage } from "./useLogisticsOperation"
import { PARCELS_LIMIT } from "./ParcelSelector"
import { AfterShipmentDialog, ReasonField } from "./AfterShipmentDialogs"
import { AWB, httpsLink } from "./TransportFields"

// ——— The sheet's validation and its reopening (D12, D13, D17; CONTEXT „Validarea fișei”, „Pașii înapoi ai fișei”) ———

// The note the API takes with the sheet's transport (common/logistics): at most 500 characters.
const NOTE_LIMIT = 500
// Refusals the sheet answers by changing: the preview is read again before another try.
const STALE = ["LOGISTICS_REVISION_CONFLICT", "LOGISTICS_SHIPMENT_BLOCKED"]

// The transport as the dialog holds it: Cargus by default, without the AWB, or the AWB the main shipment holds since a reopening, given again;
// one Cargus generated (`generated`) is kept as it is, or dropped (Tudor, 2026-10-23).
const sheetForm = held => ({ transport: "courier", courier: held?.courier || "cargus", courierName: held?.courierName || "", addAwb: Boolean(held?.awb),
  awb: held?.awb || "", parcels: held?.parcels ? String(held.parcels) : "1", trackingUrl: held?.courier && held.courier !== "cargus" ? held.trackingUrl || "" : "",
  note: "", ballsConfirmed: false, generated: held?.awbSource === "generated" ? held.awb : null })
// Whether the validation keeps the AWB Cargus generated: with Cargus, ticked; any other choice deletes it at Cargus.
const keepsGenerated = form => Boolean(form.generated) && form.transport === "courier" && form.courier === "cargus" && form.addAwb
// Whether the validation states an AWB: one entered now, or the one Cargus generated, kept.
const statesAwb = form => form.addAwb && (!form.generated || keepsGenerated(form))
// What is wrong with the transport's fields, by field, and whether it says all its transport needs: another courier's name, the AWB and its
// parcels once it is added now, or the note without a courier. The AWB Cargus generated is the server's, never checked again.
const checkSheetTransport = form => {
  const courier = form.transport === "courier"
  const awb = form.awb.trim()
  const withAwb = courier && form.addAwb && !form.generated
  const linkable = withAwb && form.courier !== "cargus"
  const problems = {
    awb: withAwb && awb && !AWB.test(awb) ? logisticsMessage("INVALID_LOGISTICS_AWB") : null,
    parcels: withAwb && !(/^\d+$/.test(form.parcels) && Number(form.parcels) >= 1 && Number(form.parcels) <= PARCELS_LIMIT)
      ? `Enter a whole number of parcels, from 1 to ${PARCELS_LIMIT}.` : null,
    trackingUrl: linkable && form.trackingUrl.trim() && !httpsLink(form.trackingUrl.trim()) ? "Paste an https link, or leave it empty." : null,
  }
  const complete = courier ? (form.courier !== "other" || Boolean(form.courierName.trim())) && (!withAwb || AWB.test(awb)) : Boolean(form.note.trim())
  return { problems, valid: complete && Object.values(problems).every(problem => !problem) }
}
// The transport as the command states it: the courier, another courier's name, the AWB with its parcels and DPD's or another courier's link
// once added now, and the note, optional with a courier; or the note alone without one. Never a day: nothing leaves at this step.
const sheetTransportBody = form => {
  const note = form.note.trim()
  if (form.transport !== "courier") return { transport: "noCourier", note }
  const linkable = form.courier !== "cargus"
  return { transport: "courier", courier: form.courier, ...(form.courier === "other" ? { courierName: form.courierName.trim() } : {}),
    ...(statesAwb(form) ? { awb: form.awb.trim(), parcels: Number(form.parcels), ...(linkable && form.trackingUrl.trim() ? { trackingUrl: form.trackingUrl.trim() }
      : {}) } : {}), ...(note ? { note } : {}) }
}
// What the email with the requirements carries, by how the main shipment will travel (D18).
const emailEffect = form => {
  if (form.transport !== "courier") return "Email „Event requirements” to the contact person and the organizer: the items are handed over without a courier."
  return statesAwb(form) ? "Email „Event requirements” with the AWB to the contact person and the organizer."
    : "Email „Event requirements” to the contact person and the organizer. No AWB yet: they get it later, in a short email."
}
// What a new validation emails (D18, Phase 11; Tudor, 2026-10-22): the requirements again, updated, only if what the organizer sees, or who
// receives it, changed since the last requirements email (`lastNeedAt`, in the event's time zone); with a shipment, the short AWB email when
// only its AWB is new.
const updatedEffect = ({ lastNeedAt, timezone }, withShipment) => "Email „Event requirements (updated)” to the contact person and the organizer, only if "
  + `what they see, or who receives it, changed since the last one (${logTime(lastNeedAt, timezone)})`
  + `${withShipment ? ": the short AWB email if only the AWB is new" : ""}; otherwise no email.`

// „Validate sheet” (D13): the coordinator chooses the transport, with a courier (Cargus, DPD or another one, named) and, when already known,
// the AWB with its parcels (DPD's or another courier's tracking link may be pasted), or without one; a note the organizer and the club see,
// required without a courier. It shows what the main shipment will send, what the club provides, what blocks the transport chosen and
// what to heed (balls the club provides are confirmed), and what the validation does. With nothing to send from MPG, it takes no transport
// and shows what the club provides: the sheet becomes No shipment (D12). It is bound to the preview's revision: a sheet that changed since
// is read again.
export function ValidateSheetDialog({ eventId, sheet, pending, onSubmit, onClose }) {
  const [preview, setPreview] = useState({ loading: true })
  const [attempt, setAttempt] = useState(0)
  const [form, setForm] = useState(() => sheetForm(mainShipment(sheet)?.heldAwb))
  const [failure, setFailure] = useState(null)
  useEffect(() => {
    let active = true
    setPreview(previous => ({ ...previous, loading: true, error: false }))
    get(`/admin/logistics/events/${eventId}/shipments/validation`).then(response => {
      if (active) setPreview({ data: response.data })
    }).catch(() => { if (active) setPreview({ error: true }) })
    return () => { active = false }
  }, [eventId, attempt])
  // The value is read before the update: React restores a controlled field before an updater runs.
  const set = fields => { setFailure(null); setForm(previous => ({ ...previous, ...fields })) }
  const change = field => event => set({ [field]: event.target.value })
  const reread = () => { setFailure(null); setAttempt(value => value + 1) }

  const data = preview.data
  // Nothing to send from MPG (D12): the validation takes no transport and leaves the sheet without a shipment.
  const nothing = nothingToSend(data)
  // What blocks and what to heed: a validation without a shipment's, or the transport's chosen.
  const readinessKey = nothing ? "noShipment" : form.transport
  const courier = !nothing && form.transport === "courier"
  const blockers = data?.blockers[readinessKey] || []
  const warnings = data?.warnings[readinessKey] || []
  const balls = warnings.find(warning => warning.kind === "balls")
  const { problems, valid } = checkSheetTransport(form)
  const ready = data && !blockers.length && (nothing || valid) && (!balls || form.ballsConfirmed)
  const clubLines = sheet.lines.filter(line => line.supplier === "club" && line.effective > 0)
  const club = clubLines.map(line => `${lineName(line, sheet.officialBall)} ${quantityLabel(line.effective, line.unit)}`)

  const submit = () => {
    setFailure(null)
    onSubmit({ action: "validateSheet", revision: data.revision, ...(nothing ? {} : sheetTransportBody(form)) }).then(result => {
      if (result && !["saved", "cancelled"].includes(result.status)) setFailure(result)
    })
  }

  return <Modal isOpen centered size="lg" toggle={onClose} labelledBy="logistics-validate-sheet-title">
    <ModalHeader id="logistics-validate-sheet-title" toggle={onClose}>Validate sheet</ModalHeader>
    <ModalBody>
      <p className="small text-muted">The coordinator's check. Then the quantities are locked, the organizer sees the requirements in SportyaOS and receives
        them by email.</p>
      {preview.loading && !data && <p role="status">Reading the sheet…</p>}
      {preview.error && <Alert color="danger">The sheet could not be read. <Button color="link" className="p-0 align-baseline" onClick={reread}>Retry</Button></Alert>}
      {data && nothing && <>
        <p>Nothing to ship from MPG. The club provides every product, so the sheet becomes No shipment: no transport, no AWB, no stock movement.</p>
        <p className="mb-1"><strong>Provided by the club</strong></p>
        <Table size="sm" className="logistics-stack mb-1" aria-label="Provided by the club">
          <tbody>{clubLines.map(line => <tr key={line.key}>
            <td>{lineName(line, sheet.officialBall)}</td>
            <td className="text-right" data-label="Quantity">{quantityLabel(line.effective, line.unit)}</td>
          </tr>)}</tbody>
        </Table>
      </>}
      {data && !nothing && <>
        <FormGroup tag="fieldset">
          <legend className="col-form-label pt-0">Transport</legend>
          {[["courier", "With a courier"], ["noCourier", "Without courier"]].map(([transport, label]) => <FormGroup check inline key={transport}>
            <Input id={`logistics-sheet-transport-${transport}`} type="radio" name="logistics-sheet-transport" checked={form.transport === transport}
              onChange={() => set({ transport })} />
            <Label check for={`logistics-sheet-transport-${transport}`}>{label}</Label>
          </FormGroup>)}
        </FormGroup>
        {courier && <div className="form-row">
          <FormGroup className="col-sm-4">
            <Label for="logistics-sheet-courier">Courier</Label>
            <Input id="logistics-sheet-courier" type="select" value={form.courier} onChange={change("courier")}>
              {Object.entries(courierNames).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </Input>
          </FormGroup>
          {form.courier === "other" && <FormGroup className="col-sm-8">
            <Label for="logistics-sheet-courier-name">Courier name</Label>
            <Input id="logistics-sheet-courier-name" maxLength={60} value={form.courierName} onChange={change("courierName")} placeholder="e.g. Speedy" />
          </FormGroup>}
          <FormGroup check className="col-12 mb-2 ml-1">
            <Input id="logistics-sheet-add-awb" type="checkbox" checked={form.addAwb} onChange={event => set({ addAwb: event.target.checked })} />
            <Label check for="logistics-sheet-add-awb">{form.generated ? `Keep AWB ${form.generated}, generated through Cargus` : "Add the AWB now"}</Label>
          </FormGroup>
          {form.generated ? <p className="col-12 small">{keepsGenerated(form)
            ? `AWB ${form.generated} · ${plural(Number(form.parcels), "parcel")}, generated through Cargus: it stays, with its pickup.`
            : "Or add an AWB later, at processing."}</p>
          : form.addAwb ? <>
            <FormGroup className="col-sm-8">
              <Label for="logistics-sheet-awb">AWB</Label>
              <Input id="logistics-sheet-awb" value={form.awb} onChange={change("awb")} invalid={Boolean(problems.awb)} placeholder="e.g. 1234567890" />
              {problems.awb ? <FormFeedback>{problems.awb}</FormFeedback> : <FormText>Created in the courier's application or with the AI prompt.</FormText>}
            </FormGroup>
            <FormGroup className="col-sm-4">
              <Label for="logistics-sheet-parcels">Parcels</Label>
              <Input id="logistics-sheet-parcels" type="number" min={1} max={PARCELS_LIMIT} step={1} value={form.parcels} onChange={change("parcels")}
                invalid={Boolean(problems.parcels)} />
              {problems.parcels && <FormFeedback>{problems.parcels}</FormFeedback>}
            </FormGroup>
            {form.courier !== "cargus" && <FormGroup className="col-12">
              <Label for="logistics-sheet-tracking">Tracking link (optional)</Label>
              <Input id="logistics-sheet-tracking" maxLength={500} value={form.trackingUrl} onChange={change("trackingUrl")} invalid={Boolean(problems.trackingUrl)} />
              {problems.trackingUrl ? <FormFeedback>{problems.trackingUrl}</FormFeedback> : <FormText>Shown to the club and the organizer.</FormText>}
            </FormGroup>}
          </> : <p className="col-12 small text-muted">Or add it later, at processing.</p>}
        </div>}
        <FormGroup>
          <Label for="logistics-sheet-note">Note</Label>
          <Input id="logistics-sheet-note" type="textarea" rows={2} maxLength={NOTE_LIMIT} value={form.note} onChange={change("note")}
            placeholder="e.g. picked up by the organizer at Câmpina" />
          <FormText>Shown to the organizer and the club. Required without a courier.</FormText>
        </FormGroup>

        <p className="mb-1"><strong>Content</strong><span className="text-muted small"> — effective quantities will be locked</span></p>
        {data.shipment.content.length > 0 && <Table size="sm" className="logistics-stack mb-1">
          <tbody>{data.shipment.content.map(item => <tr key={contentKey(item)}>
            <td>{itemName(item)}</td>
            <td className="text-right" data-label="Quantity">{quantityLabel(item.quantity, item.unit)}</td>
          </tr>)}</tbody>
        </Table>}
        {club.length > 0 && <p className="small mb-1">{`Club provides: ${club.join(" · ")}`}</p>}
        {data.shipment.setWeight != null ? <p className="small text-muted">{`Weight: ${shipmentWeightLabel(data.shipment)}`}</p>
          : weightLabel(data.shipment.weight) && <p className="small text-muted">{`Weight of the products: ${weightLabel(data.shipment.weight)}`}</p>}
      </>}
      {data && <>
        {/* The AWB Cargus generated, dropped by a validation without it, with another transport or another courier, is deleted at Cargus and
          its delivery canceled (Tudor, 2026-10-23). */}
        {form.generated && (nothing || !keepsGenerated(form)) && <p className="small text-danger">
          {`AWB ${form.generated} is deleted at Cargus and its Cargus delivery is canceled.`}</p>}
        {blockers.length > 0 && <Alert color="danger" className="small">
          <p className="mb-1">{courier ? "A courier cannot take this shipment yet:" : "The sheet cannot be validated yet:"}</p>
          <ul className="mb-0">{blockers.map(blocker => <li key={blocker.kind}>{blockerText(blocker, sheet)}</li>)}</ul>
        </Alert>}
        {warnings.filter(warning => warning.kind !== "balls").map(warning => <Alert key={warning.kind} color="warning" className="small py-2">{warningText(warning)}</Alert>)}
        {balls && <FormGroup check>
          <Input id="logistics-sheet-balls-confirmed" type="checkbox" checked={form.ballsConfirmed} onChange={event => set({ ballsConfirmed: event.target.checked })} />
          <Label check for="logistics-sheet-balls-confirmed">{balls.officialBall
            ? `I confirm the club provides the balls (no catalogue product covers ${balls.officialBall}).` : "I confirm the club provides the balls (Balls TBD)."}</Label>
        </FormGroup>}
        {nothing ? <ul className="small bg-light rounded p-2 pl-4 mt-3 mb-0" aria-label="What happens">
          <li>Quantities are locked. Reopen sheet unlocks them.</li>
          <li>The organizer sees the requirements in SportyaOS, all marked „Club”.</li>
          <li>{data.lastNeedAt ? updatedEffect(data, false)
            : "Email „Event requirements” to the contact person and the organizer: the club provides everything, Sportya ships nothing."}</li>
        </ul> : <ul className="small bg-light rounded p-2 pl-4 mt-3 mb-0" aria-label="What happens">
          <li>Quantities and suppliers are locked. Reopen sheet unlocks them.</li>
          <li>The organizer sees the requirements in SportyaOS.</li>
          <li>{data.lastNeedAt ? updatedEffect(data, true) : emailEffect(form)}</li>
          <li>The stock does not change now: it decreases when the shipment leaves.</li>
        </ul>}
        {failure && <Alert color="danger" className="mt-3 mb-0">{failure.status === "unknown"
          ? "Events has not confirmed the validation yet; it may still be saved. Check the action logs before trying again." : failure.message}
          {STALE.includes(failure.code) && <> <Button color="link" className="p-0 align-baseline" onClick={reread}>Read the sheet again</Button></>}
        </Alert>}
      </>}
    </ModalBody>
    <ModalFooter>
      <Button color="light" onClick={onClose}>Cancel</Button>
      <Button color="primary" disabled={pending || !ready} onClick={submit}>{pending ? "Validating…" : "Validate sheet"}</Button>
    </ModalFooter>
  </Modal>
}

// „Reopen sheet” (D17): back in preparation, with a reason, bound to the validated sheet as read (its main shipment's revision, or the
// validation's of a sheet without a shipment, which `onSubmit` adds). A sheet without a shipment has no AWB to keep.
export function ReopenSheetDialog({ withoutShipment = false, pending, failure, onSubmit, onClose }) {
  const [reason, setReason] = useState("")
  return <AfterShipmentDialog id="logistics-reopen-title" title="Reopen sheet" dirty={Boolean(reason.trim())} failure={failure} onClose={onClose}
    hint={reasonHint(reason, "reopen the sheet")} actions={<Button color="warning" disabled={pending || !reason.trim()}
      onClick={() => onSubmit({ action: "reopenSheet", reason: reason.trim() })}>{pending ? "Reopening…" : "Reopen sheet"}</Button>}>
    <p>Back to In preparation: the quantities unlock. Those that differ from today's calculation become quantities set by admin.</p>
    <p className="small text-muted">{`SportyaOS hides the requirements until the sheet is validated again.${withoutShipment ? "" : " The AWB stays with the shipment."}`}</p>
    <ReasonField id="logistics-reopen-reason" value={reason} onChange={setReason} placeholder="e.g. four more players on Tuesday" />
  </AfterShipmentDialog>
}
